import { MonetaryAmount, SearchInput } from '@components'
import { AccountType } from '@constants'
import { useBusinessContext } from '@context'
import { collectFromPages, fetchDocuments, IDocument } from '@query'
import { formatDate } from '@utils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegSquare } from 'react-icons/fa'
import { FaSquareCheck } from 'react-icons/fa6'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { Spinner } from '@root/components/ui/Primitives'
import { DraftBadge } from '@pages/DocumentPage/DraftBadge'
import {
  compareDocumentsByPreferredSign,
  getDocumentAmountForRelation
} from './relationMapperUtils'
import {
  PickerAmount,
  PickerCheckbox,
  PickerContact,
  PickerDate,
  PickerDescription,
  PickerEmpty,
  PickerFooter,
  PickerFooterAmount,
  PickerLabel,
  PickerList,
  PickerListScroll,
  PickerLoadingMore,
  PickerNumber,
  PickerRoot,
  PickerRow,
  PickerRowBody,
  PickerRowMeta
} from './RelationMapper.styled'

const PAGE_SIZE = 25

const documentMatchesSearch = (document: IDocument, search: string): boolean => {
  const query = search.trim().toLowerCase()
  if (!query) {
    return true
  }

  const haystack = [
    document.number,
    document.description,
    document.contact_name,
    document.import_data?.contact_hint,
    document.date,
    document.relation_entry_signals?.due_account_effect != null
      ? String(Math.abs(document.relation_entry_signals.due_account_effect))
      : null,
    document.balance != null ? String(Math.abs(document.balance)) : null
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(query)
}

const comparePickerDocuments = (
  left: IDocument,
  right: IDocument,
  selectedSet: Set<number>,
  preferredBalanceSign: 1 | -1 | null
): number => {
  const leftSelected = selectedSet.has(left.id) ? 1 : 0
  const rightSelected = selectedSet.has(right.id) ? 1 : 0
  if (leftSelected !== rightSelected) {
    return rightSelected - leftSelected
  }
  if (preferredBalanceSign != null) {
    return compareDocumentsByPreferredSign(left, right, preferredBalanceSign)
  }
  return 0
}

interface RelationDocumentPickerProps {
  label: string
  searchHint: string
  value: number[]
  onChange: (ids: number[]) => void
  excludeIds?: number[]
  enabled?: boolean
  targetAmount?: number | null
  selectionSummaryLabel: (count: number) => string
  accountTypes: AccountType[]
  accountTypeMatch?: 'any' | 'all'
  preferredBalanceSign?: 1 | -1 | null
  pinnedDocuments?: IDocument[]
}

export const RelationDocumentPicker: React.FC<RelationDocumentPickerProps> = ({
  label,
  searchHint,
  value,
  onChange,
  excludeIds = [],
  enabled = true,
  targetAmount = null,
  selectionSummaryLabel,
  accountTypes,
  accountTypeMatch = 'any',
  preferredBalanceSign = null,
  pinnedDocuments = []
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const accountTypeKey = `${accountTypeMatch}:${accountTypes.join(',')}`

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [slug, 'documents', 'relation-picker', { search, accountTypes: accountTypeKey }],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchDocuments(
        { slug },
        {
          page: pageParam,
          search,
          page_size: PAGE_SIZE,
          account_type: accountTypes,
          account_type_match: accountTypeMatch
        }
      ),
    enabled: enabled && accountTypes.length > 0,
    getNextPageParam: lastPage => lastPage.next,
    getPreviousPageParam: lastPage => lastPage.prev
  })

  const documents = useMemo(() => {
    const fetched = collectFromPages<IDocument>(data)
    const excludeSet = new Set(excludeIds)
    const byId = new Map<number, IDocument>()
    const normalizedSearch = search.trim()

    // Keep already-linked docs visible only when they match the current search
    // (or when search is empty). Otherwise random search still shows pinned rows.
    pinnedDocuments.forEach(document => {
      if (excludeSet.has(document.id)) {
        return
      }
      if (!documentMatchesSearch(document, normalizedSearch)) {
        return
      }
      byId.set(document.id, document)
    })

    fetched.forEach(document => {
      if (!excludeSet.has(document.id)) {
        byId.set(document.id, document)
      }
    })

    const merged = Array.from(byId.values())
    const selectedSet = new Set(value)

    return merged.sort((left, right) =>
      comparePickerDocuments(left, right, selectedSet, preferredBalanceSign)
    )
  }, [data, excludeIds, pinnedDocuments, preferredBalanceSign, search, value])

  const documentsByIdRef = useRef<Map<number, IDocument>>(new Map())

  const cacheDocument = (document: IDocument) => {
    documentsByIdRef.current.set(document.id, document)
    queryClient.setQueryData([slug, 'documents', document.id], document)
  }

  useEffect(() => {
    pinnedDocuments.forEach(cacheDocument)
    documents.forEach(cacheDocument)
  }, [pinnedDocuments, documents, queryClient, slug])

  const resolveSelectedDocuments = (ids: number[]): IDocument[] =>
    ids
      .map(id => documentsByIdRef.current.get(id))
      .filter((document): document is IDocument => Boolean(document))

  const selectedDocuments = useMemo(() => resolveSelectedDocuments(value), [value, documents])

  const selectedAmount = useMemo(
    () =>
      selectedDocuments.reduce(
        (total, document) => total + (getDocumentAmountForRelation(document) ?? 0),
        0
      ),
    [selectedDocuments]
  )

  const footerStatus = useMemo((): 'exact' | 'over' | 'under' | undefined => {
    if (targetAmount == null || value.length === 0) {
      return undefined
    }
    const selectedCents = Math.round(selectedAmount * 100)
    const targetCents = Math.round(targetAmount * 100)
    if (selectedCents === targetCents) {
      return 'exact'
    }
    if (selectedCents > targetCents) {
      return 'over'
    }
    return 'under'
  }, [selectedAmount, targetAmount, value.length])

  const toggleDocument = (documentId: number) => {
    const document = documentsByIdRef.current.get(documentId)
    if (document) {
      cacheDocument(document)
    }

    if (value.includes(documentId)) {
      onChange(value.filter(id => id !== documentId))
      return
    }
    onChange([...value, documentId])
  }

  const handleScroll: React.UIEventHandler<HTMLDivElement> = event => {
    if (!hasNextPage || isFetchingNextPage) return
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 48) {
      fetchNextPage()
    }
  }

  const isInitialLoading = isLoading && documents.length === 0
  const isEmpty = !isInitialLoading && documents.length === 0

  return (
    <PickerRoot>
      <PickerLabel>{label}</PickerLabel>

      <SearchInput onSearch={setSearch} placeholder={searchHint} aria-label={label} hideErrorText />

      <PickerList>
        <PickerListScroll onScroll={handleScroll}>
          {isInitialLoading ? (
            <PickerLoadingMore>
              <Spinner size={16} />
            </PickerLoadingMore>
          ) : isEmpty ? (
            <PickerEmpty>{t('documentMultiSelect.noResults', 'No documents found')}</PickerEmpty>
          ) : (
            <>
              {documents.map(document => {
                const isSelected = value.includes(document.id)
                const numberLabel = document.number?.trim() || ''
                const contactName = (
                  document.contact_name ||
                  document.import_data?.contact_hint ||
                  ''
                ).trim()
                const description = document.description?.trim() || ''

                return (
                  <PickerRow
                    key={document.id}
                    type="button"
                    onClick={() => toggleDocument(document.id)}
                    $isSelected={isSelected}
                  >
                    <PickerCheckbox $isSelected={isSelected} aria-hidden>
                      {isSelected ? <FaSquareCheck /> : <FaRegSquare />}
                    </PickerCheckbox>
                    <PickerRowBody>
                      <PickerRowMeta>
                        <PickerNumber>
                          {document.is_draft ? <DraftBadge /> : null}
                          {numberLabel || (document.is_draft ? t('document.draft') : '—')}
                        </PickerNumber>
                        <PickerDate>{document.date ? formatDate(document.date) : ''}</PickerDate>
                      </PickerRowMeta>
                      <PickerRowMeta>
                        {contactName ? (
                          <PickerContact title={contactName}>{contactName}</PickerContact>
                        ) : (
                          <span />
                        )}
                        <PickerAmount>
                          <MonetaryAmount value={getDocumentAmountForRelation(document) ?? 0} />
                        </PickerAmount>
                      </PickerRowMeta>
                      {description ? (
                        <PickerDescription title={description}>{description}</PickerDescription>
                      ) : null}
                    </PickerRowBody>
                  </PickerRow>
                )
              })}
              {isFetchingNextPage ? (
                <PickerLoadingMore>
                  <Spinner size={16} />
                </PickerLoadingMore>
              ) : null}
            </>
          )}
        </PickerListScroll>

        {value.length > 0 ? (
          <PickerFooter>
            <span>{selectionSummaryLabel(value.length)}</span>
            {targetAmount != null ? (
              <PickerFooterAmount $status={footerStatus}>
                <MonetaryAmount value={selectedAmount} /> {t('documentRelations.status.of')}{' '}
                <MonetaryAmount value={targetAmount} />
              </PickerFooterAmount>
            ) : null}
          </PickerFooter>
        ) : null}
      </PickerList>
    </PickerRoot>
  )
}
